import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit2, Users, UserPlus, X, Shield, ShieldOff } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface Business {
  id: string;
  name: string;
  description: string;
  team_leader_id: string;
  created_at: string;
}

interface TeamLeader {
  id: string;
  telegram_id: number;
  telegram_username: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

interface User {
  id: string;
  telegram_id: number;
  telegram_username: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

export default function BusinessManagement() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [teamLeaders, setTeamLeaders] = useState<TeamLeader[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const [showAssignUsers, setShowAssignUsers] = useState<string | null>(null);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [businessesResult, leadersResult, usersResult] = await Promise.all([
        supabase.from('businesses').select('*').order('created_at', { ascending: false }),
        supabase.from('users').select('id, telegram_id, telegram_username, first_name, last_name, role').eq('role', 'team_leader'),
        supabase.from('users').select('id, telegram_id, telegram_username, first_name, last_name, role'),
      ]);

      setBusinesses(businessesResult.data || []);
      setTeamLeaders(leadersResult.data || []);
      setUsers(usersResult.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBusiness = async (business: any) => {
    try {
      const { error } = await supabase.from('businesses').insert([business]);
      if (error) throw error;
      await loadData();
      setShowAddBusiness(false);
    } catch (error) {
      console.error('Error adding business:', error);
      alert('Failed to add business');
    }
  };

  const updateBusiness = async (businessId: string, updates: any) => {
    try {
      const { error } = await supabase
        .from('businesses')
        .update(updates)
        .eq('id', businessId);
      if (error) throw error;
      await loadData();
      setEditingBusiness(null);
    } catch (error) {
      console.error('Error updating business:', error);
      alert('Failed to update business');
    }
  };

  const toggleTeamLeader = async (userId: string, currentRole: string) => {
    try {
      const newRole = currentRole === 'team_leader' ? 'normal' : 'team_leader';
      const { error } = await supabase
        .from('users')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    }
  };

  const getTeamLeaderName = (leaderId: string) => {
    const leader = teamLeaders.find((l) => l.id === leaderId);
    if (!leader) return 'Unassigned';
    if (leader.first_name && leader.last_name) {
      return `${leader.first_name} ${leader.last_name}`;
    }
    return leader.first_name || leader.telegram_username || `User ${leader.telegram_id}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Business Management</h2>
        <button
          onClick={() => setShowAddBusiness(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Business
        </button>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Team Leaders</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          Promote users to team leader to assign them to businesses. Team leaders can manage their business users and view team receipts.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {users.map((user) => {
            const isTeamLeader = user.role === 'team_leader';
            return (
              <div
                key={user.telegram_id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  isTeamLeader
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                }`}
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.first_name || user.telegram_username || `User ${user.telegram_id}`}
                  </p>
                  {user.telegram_username && (
                    <p className="text-xs text-gray-700 dark:text-gray-300">@{user.telegram_username}</p>
                  )}
                </div>
                <button
                  onClick={() => toggleTeamLeader(user.id, user.role)}
                  className={`p-2 rounded-lg transition-colors ${
                    isTeamLeader
                      ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30'
                      : 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30'
                  }`}
                  title={isTeamLeader ? 'Remove team leader role' : 'Promote to team leader'}
                >
                  {isTeamLeader ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-800 dark:text-gray-200">Loading businesses...</div>
        ) : businesses.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-800 dark:text-gray-200">
            No businesses yet. Create one to get started.
          </div>
        ) : (
          businesses.map((business) => (
            <div key={business.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-brand-primary" />
                </div>
                <button
                  onClick={() => setEditingBusiness(business)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-800 dark:text-gray-200 dark:hover:text-gray-400"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{business.name}</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{business.description || 'No description'}</p>

              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-3">
                <Users className="w-4 h-4" />
                <span>Team Leader: {getTeamLeaderName(business.team_leader_id)}</span>
              </div>

              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAssignUsers(business.id)}
                  className="text-brand-primary text-sm font-medium hover:text-brand-secondary flex items-center gap-1"
                >
                  <UserPlus className="w-4 h-4" />
                  Assign Users
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddBusiness && (
        <AddBusinessModal
          onClose={() => setShowAddBusiness(false)}
          onAdd={addBusiness}
        />
      )}

      {showAssignUsers && (
        <AssignUsersModal
          businessId={showAssignUsers}
          users={users}
          onClose={() => setShowAssignUsers(null)}
          onReload={loadData}
        />
      )}

      {editingBusiness && (
        <EditBusinessModal
          business={editingBusiness}
          teamLeaders={teamLeaders}
          onClose={() => setEditingBusiness(null)}
          onUpdate={updateBusiness}
        />
      )}
    </div>
  );
}

function AddBusinessModal({ onClose, onAdd }: any) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Add New Business</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-field"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-field"
              rows={3}
            />
          </div>

          <p className="text-sm text-gray-700 dark:text-gray-300">
            After creating the business, you can assign a team leader by editing it.
          </p>

          <div className="flex gap-3 pt-4">
            <button type="submit" className="btn-primary flex-1">
              Create Business
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignUsersModal({ businessId, users, onClose, onReload }: any) {
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAssignedUsers();
  }, []);

  const loadAssignedUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_businesses')
        .select('user_id')
        .eq('business_id', businessId);

      if (error) throw error;
      setAssignedUsers(data?.map(u => u.user_id) || []);
    } catch (error) {
      console.error('Error loading assigned users:', error);
    } finally {
      setLoading(false);
    }
  };

  const assignUsers = async () => {
    try {
      const newAssignments = selectedUsers.map(userId => {
        const user = users.find((u: User) => u.telegram_id === userId);
        return {
          user_id: userId,
          business_id: businessId,
          telegram_username: user?.telegram_username,
          telegram_first_name: user?.first_name,
          telegram_last_name: user?.last_name,
        };
      });

      const { error } = await supabase
        .from('user_businesses')
        .insert(newAssignments);

      if (error) throw error;

      await onReload();
      onClose();
    } catch (error) {
      console.error('Error assigning users:', error);
      alert('Failed to assign users');
    }
  };

  const removeUser = async (userId: number) => {
    try {
      const { error } = await supabase
        .from('user_businesses')
        .delete()
        .eq('user_id', userId)
        .eq('business_id', businessId);

      if (error) throw error;
      await loadAssignedUsers();
    } catch (error) {
      console.error('Error removing user:', error);
    }
  };

  const toggleUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const availableUsers = users.filter((u: User) => !assignedUsers.includes(u.telegram_id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Assign Users to Business</h3>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <>
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Currently Assigned Users</h4>
              {assignedUsers.length === 0 ? (
                <p className="text-sm text-gray-800 dark:text-gray-200">No users assigned yet</p>
              ) : (
                <div className="space-y-2">
                  {assignedUsers.map(userId => {
                    const user = users.find((u: User) => u.telegram_id === userId);
                    if (!user) return null;
                    return (
                      <div key={userId} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {user.first_name} {user.last_name}
                          </p>
                          {user.telegram_username && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">@{user.telegram_username}</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeUser(userId)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Available Users</h4>
              {availableUsers.length === 0 ? (
                <p className="text-sm text-gray-800 dark:text-gray-200">All users are assigned</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableUsers.map((user: User) => (
                    <label
                      key={user.telegram_id}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.telegram_id)}
                        onChange={() => toggleUser(user.telegram_id)}
                        className="w-4 h-4 text-brand-primary border-gray-300 rounded focus:ring-brand-primary"
                      />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {user.first_name} {user.last_name}
                        </p>
                        {user.telegram_username && (
                          <p className="text-sm text-gray-700 dark:text-gray-300">@{user.telegram_username}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={assignUsers}
                disabled={selectedUsers.length === 0}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                Assign Selected Users ({selectedUsers.length})
              </button>
              <button onClick={onClose} className="btn-secondary flex-1">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditBusinessModal({ business, teamLeaders, onClose, onUpdate }: any) {
  const [formData, setFormData] = useState({
    name: business.name,
    description: business.description,
    team_leader_id: business.team_leader_id,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(business.id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Edit Business</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-field"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-field"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Team Leader</label>
            <select
              value={formData.team_leader_id}
              onChange={(e) => setFormData({ ...formData, team_leader_id: e.target.value })}
              className="input-field"
            >
              <option value="">Select a team leader</option>
              {teamLeaders.map((leader: any) => (
                <option key={leader.id} value={leader.id}>
                  {leader.first_name && leader.last_name
                    ? `${leader.first_name} ${leader.last_name}`
                    : leader.first_name || leader.telegram_username || `User ${leader.telegram_id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" className="btn-primary flex-1">
              Save Changes
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
